import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Star, Zap, Fuel, GitCompareArrows, Bike, Bell, IndianRupee, Wallet, ShieldCheck, Bluetooth, Gauge, Battery, MapPin, MessageCircle, Phone } from "lucide-react";
import api, { formatPriceRange, formatPrice, ytEmbed, resolveAsset } from "@/lib/api";
import BikeCard from "@/components/site/BikeCard";
import LeadModal from "@/components/site/LeadModal";
import StickyInquiryBar from "@/components/site/StickyInquiryBar";
import PageSeo from "@/components/site/PageSeo";
import { useSettings } from "@/contexts/SettingsContext";
import { bikeDetailSeo } from "@/lib/seo";
import { toast } from "sonner";
import { addToCompare, compareUrl, getCompareIds } from "@/lib/compare";
import {
    resolveWhatsAppDigits,
    resolveTelHref,
    whatsappUrl,
    bikeInquiryMessage,
    emiInquiryMessage,
} from "@/lib/contact";
import { selectOption } from "@/lib/selectOption";

const SpecRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-center gap-3 py-3 border-b border-[#e5e7eb]" data-testid={`spec-${label.replace(/\s/g, '').toLowerCase()}`}>
        <div className="w-9 h-9 rounded-lg bg-[#fff5f5] flex items-center justify-center text-[#ED1C24]"><Icon size={16} /></div>
        <div>
            <p className="text-xs text-[#7a7a84]">{label}</p>
            <p className="font-display font-semibold text-sm">{value}</p>
        </div>
    </div>
);

const BikeDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { settings, pages } = useSettings();
    const [bike, setBike] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [similar, setSimilar] = useState([]);
    const [mainImg, setMainImg] = useState("");
    const [color, setColor] = useState(0);

    const [leadOpen, setLeadOpen] = useState(false);
    const [leadIntent, setLeadIntent] = useState("test_ride");
    const [leadNote, setLeadNote] = useState("");

    const downPct = (pages?.emi_down_pct ?? 15) / 100;
    const downPctLabel = Math.round((pages?.emi_down_pct ?? 15) * 10) / 10;
    const interest = pages?.emi_default_rate ?? 14;
    const tenureOptions = useMemo(() => {
        const fromCms = (pages?.emi_tenures || "18,24,30,36")
            .split(",")
            .map((n) => parseInt(n.trim(), 10))
            .filter(Boolean);
        return fromCms.length ? fromCms : [18, 24, 30, 36];
    }, [pages?.emi_tenures]);
    const [emiTenure, setEmiTenure] = useState(tenureOptions[1] || 24);
    const [compareIds, setCompareIds] = useState(getCompareIds);

    const waDigits = resolveWhatsAppDigits(settings);
    const telHref = resolveTelHref(settings);

    useEffect(() => {
        setBike(null);
        setNotFound(false);
        setSimilar([]);
        setMainImg("");
        setColor(0);
        setLeadOpen(false);
        setLeadIntent("test_ride");
        setLeadNote("");
        const tenures = (pages?.emi_tenures || "18,24,30,36")
            .split(",")
            .map((n) => parseInt(n.trim(), 10))
            .filter(Boolean);
        setEmiTenure(tenures[1] || tenures[0] || 24);

        const ctrl = new AbortController();
        api.get(`/bikes/${id}`, { signal: ctrl.signal })
            .then((r) => {
                setBike(r.data);
                setMainImg(r.data.image);
            })
            .catch((err) => {
                if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
                setNotFound(true);
            });
        api.get(`/bikes/${id}/similar`, { signal: ctrl.signal })
            .then((r) => setSimilar(r.data || []))
            .catch((err) => {
                if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
                setSimilar([]);
            });
        return () => ctrl.abort();
        // pages.emi_tenures read only to reset default tenure on bike change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        const sync = () => setCompareIds(getCompareIds());
        window.addEventListener("compare-updated", sync);
        return () => window.removeEventListener("compare-updated", sync);
    }, []);

    const handleCompare = () => {
        const result = addToCompare(bike.id);
        if (result.full && !result.added) {
            toast.error("Shortlist full (3 bikes). Open Compare to manage.");
            navigate(compareUrl(result.ids));
            return;
        }
        if (result.added) {
            toast.success(`${bike.name} added to compare shortlist`);
        } else {
            toast.info(`${bike.name} is already in your shortlist`);
        }
        if (result.ids.length >= 2) {
            navigate(compareUrl(result.ids));
        }
    };

    const handleCompareCard = (b) => {
        const result = addToCompare(b.id);
        if (result.added) toast.success(`${b.name} added to compare shortlist`);
        else if (result.full) toast.error("Shortlist full (3 bikes). Remove one or go to Compare.");
        else toast.info(`${b.name} is already in your shortlist`);
    };

    const emi = useMemo(() => {
        if (!bike) return null;
        const price = bike.price_min;
        const down = Math.round(price * downPct);
        const loan = price - down;
        const r = interest / 12 / 100;
        const n = emiTenure;
        const m = (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
        const total = m * n;
        return { down, loan, monthly: Math.round(m), total: Math.round(total), interestAmt: Math.round(total - loan) };
    }, [bike, emiTenure, interest, downPct]);

    const openLead = (intent, note = "") => {
        setLeadIntent(intent);
        setLeadNote(note);
        setLeadOpen(true);
    };

    if (notFound) {
        return (
            <div className="container-x py-20 text-center" data-testid="bike-not-found">
                <h1 className="font-display font-bold text-3xl text-[#0A2540]">Bike not found</h1>
                <p className="text-sm text-[#4a4a52] mt-2">This model may have been removed. Browse the catalogue instead.</p>
                <Link to="/bikes" className="btn btn-primary mt-6">All bikes</Link>
            </div>
        );
    }

    if (!bike) return <div className="container-x py-20 text-center text-[#7a7a84]">Loading…</div>;

    const isElectric = bike.fuel_type === "Electric";
    const upcoming = Boolean(bike.is_upcoming);
    const waDealHref = whatsappUrl(waDigits, bikeInquiryMessage(bike, upcoming ? "pre_book" : "whatsapp_inquiry"));
    const videoEmbed = ytEmbed(bike.youtube_url);
    const seo = bikeDetailSeo(bike, pages, settings);

    return (
        <section className="container-x py-10 pb-28" data-testid="bike-detail-page">
            <PageSeo pageKey="bike_detail" title={seo.title} description={seo.description} path={`/bikes/${id}`} image={seo.image} />
            <div className="text-sm text-[#7a7a84] mb-5">
                <Link to="/" className="hover:text-[#ED1C24]">Home</Link> /{" "}
                <Link to="/bikes" className="hover:text-[#ED1C24]">Bikes</Link> /{" "}
                <span className="text-[#0f0f12]">{bike.name}</span>
            </div>

            {upcoming && (
                <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="bike-upcoming-banner">
                    <strong>Coming soon.</strong> This model is not on the floor yet. Register your interest and we&apos;ll notify you when it&apos;s available for test ride and booking at your nearest outlet.
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-[#f3f4f6]" data-testid="bike-main-image">
                        <img src={resolveAsset(mainImg)} alt={bike.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex gap-3 mt-3 flex-wrap">
                        {(bike.images || []).map((img, i) => (
                            <button key={i} onClick={() => setMainImg(img)}
                                className={`w-20 h-20 rounded-lg overflow-hidden border-2 ${mainImg === img ? "border-[#ED1C24]" : "border-transparent"}`}>
                                <img src={resolveAsset(img)} alt={`view ${i}`} className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <span className="chip chip-brand">{bike.brand}</span>
                    <h1 className="font-display font-bold text-3xl md:text-4xl mt-3" data-testid="bike-name">{bike.name}</h1>
                    <div className="flex items-center gap-3 mt-3 text-sm text-[#4a4a52]">
                        <span className="flex items-center gap-1"><Star size={14} className="fill-[#ED1C24] text-[#ED1C24]" /> {bike.rating?.toFixed(1)}</span>
                        <span>•</span>
                        <span className="chip chip-red text-[10px]">{isElectric ? "Electric" : "Petrol"}</span>
                        <span>•</span>
                        <span>{bike.subcategory}</span>
                    </div>
                    <div className="mt-5">
                        <p className="text-xs text-[#7a7a84]">{upcoming ? "Expected ex-showroom" : "Ex-showroom price"}</p>
                        <p className="font-display font-bold text-3xl text-[#ED1C24]" data-testid="bike-price">
                            {upcoming ? "Enquire for price" : formatPriceRange(bike.price_min, bike.price_max)}
                        </p>
                    </div>

                    {bike.colors?.length > 0 && (
                        <div className="mt-5">
                            <p className="label">Colors ({bike.colors.length})</p>
                            <div className="flex gap-2 flex-wrap">
                                {bike.colors.map((c, i) => (
                                    <button key={i} onClick={() => setColor(i)}
                                        className={`px-3 py-1.5 min-h-[44px] rounded-full text-xs border-2 ${color === i ? "border-[#ED1C24] text-[#ED1C24]" : "border-[#e5e7eb] text-[#4a4a52]"}`}>
                                        {c}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mt-6 grid grid-cols-2 gap-2">
                        {upcoming ? (
                            <>
                                <button onClick={() => openLead("pre_book", `Pre-book interest for ${bike.name}. Please call back when available.`)} className="btn btn-primary btn-sm justify-center col-span-2 min-h-[44px]" data-testid="detail-pre-book">
                                    Register interest
                                </button>
                                <a href={waDealHref} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm justify-center text-[#25D366] border-[#25D366]/40 min-h-[44px] col-span-2" data-testid="detail-whatsapp">
                                    <MessageCircle size={14} /> WhatsApp enquiry
                                </a>
                            </>
                        ) : (
                            <>
                                <button onClick={() => openLead("test_ride")} className="btn btn-primary btn-sm justify-center col-span-2 min-h-[44px]" data-testid="detail-test-ride">
                                    <Bike size={14} /> Book test ride
                                </button>
                                <a href={telHref} className="btn btn-ghost btn-sm justify-center min-h-[44px] sm:hidden" data-testid="detail-call">
                                    <Phone size={14} /> Call
                                </a>
                                <a href={waDealHref} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm justify-center text-[#25D366] border-[#25D366]/40 min-h-[44px] sm:hidden" data-testid="detail-whatsapp">
                                    <MessageCircle size={14} /> WhatsApp
                                </a>
                                <button onClick={() => openLead("whatsapp_inquiry")} className="btn btn-ghost btn-sm justify-center min-h-[44px]" data-testid="detail-best-deal">
                                    <Wallet size={14} /> Get on-road quote
                                </button>
                                <button onClick={() => openLead("price_drop")} className="btn btn-ghost btn-sm justify-center min-h-[44px]" data-testid="detail-price-alert">
                                    <Bell size={14} /> Price update
                                </button>
                                <button type="button" onClick={handleCompare} className="btn btn-white btn-sm justify-center min-h-[44px] col-span-2 sm:col-span-1" data-testid="detail-compare">
                                    <GitCompareArrows size={14} /> Compare
                                </button>
                                <a href={telHref} className="hidden sm:inline-flex btn btn-ghost btn-sm justify-center" data-testid="detail-call-desktop">
                                    <Phone size={14} /> Call outlet
                                </a>
                                <a href={waDealHref} target="_blank" rel="noopener noreferrer" className="hidden sm:inline-flex btn btn-ghost btn-sm justify-center text-[#25D366] border-[#25D366]/40" data-testid="detail-whatsapp-desktop">
                                    <MessageCircle size={14} /> WhatsApp
                                </a>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bike-card p-6">
                    <h2 className="font-display font-bold text-xl mb-4">Key specifications</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                        {!isElectric && <SpecRow icon={Bike} label="Engine" value={`${bike.engine_cc} cc`} />}
                        {isElectric && <SpecRow icon={Battery} label="Battery" value={`${bike.battery_kwh} kWh`} />}
                        <SpecRow icon={Gauge} label="Power" value={`${bike.power_bhp} bhp`} />
                        <SpecRow icon={Zap} label="Torque" value={`${bike.torque_nm} Nm`} />
                        {!isElectric && <SpecRow icon={Fuel} label="Mileage" value={`${bike.mileage} kmpl`} />}
                        {isElectric && <SpecRow icon={MapPin} label="Range" value={`${bike.range_km} km`} />}
                        <SpecRow icon={Bike} label="Weight" value={`${bike.weight} kg`} />
                        <SpecRow icon={ShieldCheck} label="ABS" value={bike.abs ? "Yes" : "No"} />
                        {bike.bluetooth && <SpecRow icon={Bluetooth} label="Bluetooth" value="Yes" />}
                    </div>

                    <div className="mt-6">
                        <h3 className="font-display font-bold text-lg mb-2">About this bike</h3>
                        <p className="text-[#4a4a52] leading-relaxed text-sm">{bike.description}</p>
                    </div>

                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h4 className="font-display font-bold text-sm text-[#10B981] uppercase tracking-wider mb-2">Pros</h4>
                            <ul className="space-y-1 text-sm text-[#4a4a52]">
                                {(bike.pros || []).map((p, i) => <li key={i}>• {p}</li>)}
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-display font-bold text-sm text-[#ED1C24] uppercase tracking-wider mb-2">Cons</h4>
                            <ul className="space-y-1 text-sm text-[#4a4a52]">
                                {(bike.cons || []).map((p, i) => <li key={i}>• {p}</li>)}
                            </ul>
                        </div>
                    </div>
                </div>

                {!upcoming && (
                    <div className="bike-card p-6 h-fit" data-testid="emi-mini">
                        <div className="flex items-center gap-2 mb-3">
                            <IndianRupee size={18} className="text-[#ED1C24]" />
                            <h3 className="font-display font-bold text-lg">EMI calculator</h3>
                        </div>
                        <div className="text-xs text-[#7a7a84]">Bike price</div>
                        <div className="font-display font-bold text-xl">{formatPrice(bike.price_min)}</div>
                        <div className="mt-3 text-xs text-[#7a7a84]">Down payment ({downPctLabel}%)</div>
                        <div className="font-display font-semibold">{formatPrice(emi?.down || 0)}</div>
                        <div className="mt-3 text-xs text-[#7a7a84]">Tenure</div>
                        <select value={emiTenure} onChange={(e) => setEmiTenure(parseInt(e.target.value))} className="input mt-1" data-testid="emi-mini-tenure">
                            {tenureOptions.map((t) => selectOption(t, `${t} months`))}
                        </select>
                        <div className="mt-4 p-4 rounded-xl bg-[#fff5f5]">
                            <p className="text-xs text-[#7a7a84]">Indicative EMI @ {interest}% p.a.</p>
                            <p className="font-display font-black text-2xl text-[#ED1C24]" data-testid="emi-mini-amount">₹{emi?.monthly?.toLocaleString("en-IN")}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => openLead(
                                "loan_inquiry",
                                emiInquiryMessage({ monthly: emi?.monthly, tenure: emiTenure, price: bike.price_min }),
                            )}
                            className="btn btn-primary btn-sm w-full justify-center mt-3"
                            data-testid="emi-mini-apply"
                        >
                            Enquire about EMI
                        </button>
                        <Link to={`/emi?price=${bike.price_min}&bike=${bike.id}`} className="btn btn-white btn-sm w-full justify-center mt-2" data-testid="emi-mini-full">Full calculator</Link>
                    </div>
                )}
            </div>

            {videoEmbed && (
                <div className="mt-12" data-testid="bike-video">
                    <h2 className="font-display font-bold text-2xl mb-4">Video walkthrough</h2>
                    <div className="aspect-video rounded-2xl overflow-hidden bike-card">
                        <iframe className="w-full h-full" src={videoEmbed} title="Bike walkthrough" allow="encrypted-media" allowFullScreen />
                    </div>
                </div>
            )}

            {similar.length > 0 && (
                <div className="mt-12">
                    <h2 className="font-display font-bold text-2xl mb-4">Similar bikes</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {similar.slice(0, 3).map(b => (
                            <BikeCard
                                key={b.id}
                                bike={b}
                                onCompare={handleCompareCard}
                                inCompare={compareIds.includes(b.id)}
                            />
                        ))}
                    </div>
                </div>
            )}

            <LeadModal open={leadOpen} onOpenChange={setLeadOpen} intent={leadIntent} bike={bike} note={leadNote} />
            <StickyInquiryBar
                bike={bike}
                upcoming={upcoming}
                onPrimary={() => openLead(upcoming ? "pre_book" : "test_ride", upcoming ? `Pre-book interest for ${bike.name}.` : "")}
            />
        </section>
    );
};

export default BikeDetailPage;
